import { useAuth } from "../contexts/AuthContext";
import axiosInstance from "../api/axiosInstance";
import { useEffect } from "react";

let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(token) {
	refreshSubscribers.forEach((cb) => cb(token));
	refreshSubscribers = [];
}

function addRefreshSubscriber(callback) {
	refreshSubscribers.push(callback);
}

const useAxiosWithAuthInfoAuto = () => {
	const { getAccessToken, refreshToken } = useAuth();

	useEffect(() => {
		const requestInterceptor = axiosInstance.interceptors.request.use(
			async (config) => {
				const token = await getAccessToken();
				console.log("Token enviado:", token);
				config.headers.Authorization = `Bearer ${token}`;
				return config;
			},
			(error) => Promise.reject(error)
		);

		const responseInterceptor = axiosInstance.interceptors.response.use(
			(response) => response,
			async (error) => {
				const originalRequest = error.config;

				if (
					error.response &&
					(error.response.status === 301 || error.response.status === 308) &&
					error.response.headers.location
				) {
					const redirectUrl = error.response.headers.location;
					const token = await getAccessToken();

					return axiosInstance({
						...originalRequest,
						url: redirectUrl,
						headers: {
							...originalRequest.headers,
							Authorization: `Bearer ${token}`,
						},
					});
				}

				if (
					error.response &&
					error.response.status === 401 &&
					!originalRequest._retry
				) {
					originalRequest._retry = true;

					if (!isRefreshing) {
						isRefreshing = true;

						try {
							const newToken = await refreshToken();
							onRefreshed(newToken);
							isRefreshing = false;
							return axiosInstance(originalRequest);
						} catch (err) {
							isRefreshing = false;
							return Promise.reject(err);
						}
					}

					return new Promise((resolve) => {
						addRefreshSubscriber((token) => {
							originalRequest.headers.Authorization = `Bearer ${token}`;
							resolve(axiosInstance(originalRequest));
						});
					});
				}

				return Promise.reject(error);
			}
		);

		return () => {
			axiosInstance.interceptors.request.eject(requestInterceptor);
			axiosInstance.interceptors.response.eject(responseInterceptor);
		};
	}, [getAccessToken, refreshToken]);

	const fetchWithAuth = async (url, options = {}) => {
		const method = options.method || "get";
		const data = options.body ? JSON.parse(options.body) : undefined;

		const response = await axiosInstance({
			url,
			method,
			data,
			...options,
		});

		return response.data;
	};

	return fetchWithAuth;
};

export { useAxiosWithAuthInfoAuto };
